import { useEffect, useState, type RefObject } from 'react';

/**
 * Whether an element has come near the viewport. Once seen it stays true:
 * the callers mount images and masks on it, and those must not unmount
 * again while the user scrolls back. Where no IntersectionObserver exists
 * everything counts as in view, so nothing is ever withheld.
 */
export function useInView(
  ref: RefObject<Element | null>,
  rootMargin = '50% 0px'
): boolean {
  const supported = typeof IntersectionObserver !== 'undefined';
  const [inView, setInView] = useState(!supported);

  useEffect(() => {
    if (!supported || inView) return;
    const element = ref.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref, rootMargin, supported, inView]);

  return inView;
}
